// @flow

import * as React from 'react';
import {
  useRelayEnvironment,
  QueryRenderer,
  graphql,
  useFragment,
} from 'react-relay';
import type { RenderProps } from 'react-relay';
import { Flex, Box } from 'react-system';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { parseISO, format, formatDistanceStrict } from 'date-fns';
import { Image } from '@realadvisor/image';
import { Phone } from '../../icons/phone';
import { Link } from '../../controls/link';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../theme';
import { InlineMarked } from '../../controls/marked';
import { useUserData } from '../../hooks/user-data.js';
import { RatingComponent } from '../components/RatingComponent';
import { getAgencyLink } from '../../utils/agency-link.js';
import { AggregatesListingCampaignEnquiry } from './aggregates-listing-campaign-enquiry.js';

import type { AggregatesListingPriceCard_listing$key } from './__generated__/AggregatesListingPriceCard_listing.graphql';
import type { AggregatesListingPriceCardContent_listing$key } from './__generated__/AggregatesListingPriceCardContent_listing.graphql';
import type { AggregatesListingPriceCardMeQueryResponse } from './__generated__/AggregatesListingPriceCardMeQuery.graphql';
type Props = {|
  listing: AggregatesListingPriceCard_listing$key,
  onLinkoutClick: (
    $ReadOnly<{|
      url: string,
      originalDomain: string,
      mergedListingId: string,
    |}>,
  ) => void,
  isListingCampaign?: boolean,
|};

type ContentProps = {|
  listing: AggregatesListingPriceCardContent_listing$key,
|};

export const domainToSitename = (domain: string): string => {
  return domain.replace('www.', '');
};

const AggregatesListingPriceCardContent = (props: ContentProps) => {
  const listing = useFragment(
    graphql`
      fragment AggregatesListingPriceCardContent_listing on AggregatesListing {
        createdAt
        currency
        offlineSince
      }
    `,
    props.listing,
  );

  const { text } = useTheme();
  const { t, dateLocale } = useLocale();

  const { createdAt, offlineSince } = listing;

  return (
    <>
      <Box mt={3}>
        {offlineSince == null && (
          <>
            <Flex>
              <Box flexGrow={1} flexBasis="160px" css={text.subtitle1}>
                {t('onlineSince')}
              </Box>
              <Box
                css={[text.body1, { textAlign: 'right' }]}
                flexGrow={1}
                ml={2}
              >
                {format(parseISO(createdAt), 'dd/MM/yyyy')}
              </Box>
            </Flex>
            <Box css={[text.caption, { textAlign: 'right' }]} pb={3}>
              {formatDistanceStrict(parseISO(createdAt), new Date(), {
                locale: dateLocale,
                addSuffix: true,
              })}
            </Box>
          </>
        )}

        <Box pt={3}>
          <Divider />
        </Box>
        <Box py={3} css={text.body2}>
          <InlineMarked>{t('getImprovedEstimateText')}</InlineMarked>
        </Box>
        <Link
          href={{
            pathname: '/appraisal',
          }}
        >
          <Button
            color="primary"
            size="small"
            component="a"
            variant="outlined"
            target="_blank"
            rel="noopener"
          >
            {t('estimateThisProperty')}
          </Button>
        </Link>
      </Box>
    </>
  );
};

export const AggregatesListingPriceCard = (props: Props): React.Node => {
  const environment = useRelayEnvironment();
  const { colors, text, shape } = useTheme();
  const {
    t,
    language,
    numberLocale,
    dateLocale,
    currency: fallbackCurrency,
  } = useLocale();
  const { countryDomain } = useUserData();
  const listing = useFragment(
    /* eslint-disable relay/unused-fields */
    graphql`
      fragment AggregatesListingPriceCard_listing on AggregatesListing {
        ...AggregatesListingPriceCardContent_listing
        ...aggregatesListingCampaignEnquiry_listing
        salePrice
        offerType
        grossRentMonthly
        grossRentM2yearly
        salePricePerLivingSurface
        salePricePerLandSurface
        currency
        offlineSince
        createdAt
        bookOnline
        id
        slug
        offerType
        agencyId
        agencyName
        agencySlug
        agencyLogoUrl
        agencyRating
        agencyReviewsCount
        agencyReference
        agencyContactPhoneNumber
        visitContactPerson
        visitContactPhoneNumber
        url {
          originalDomain
        }
        urls {
          url
          originalDomain
          mergedListingId
        }
      }
    `,
    props.listing,
  );
  const {
    salePrice,
    offerType,
    grossRentMonthly,
    grossRentM2yearly,
    salePricePerLivingSurface,
    salePricePerLandSurface,
    urls,
    currency,
    offlineSince,
    createdAt,
    bookOnline,
    agencyId,
    agencyName,
    agencySlug,
    agencyLogoUrl,
    agencyRating,
    agencyReviewsCount,
    agencyContactPhoneNumber,
    visitContactPhoneNumber,
  } = listing;

  const salePricePerComputedSurface =
    salePricePerLivingSurface != null
      ? salePricePerLivingSurface
      : salePricePerLandSurface;

  const [showPhoneNumber, setShowPhoneNumber] = React.useState(false);

  const handleShowPhoneNumber = setStateAction => {
    // Adding delay so link href attribute is set after click event
    // and link won't be opened when toggling phone number.
    setTimeout(() => {
      setStateAction(true);
    }, 250);

    window.dataLayer.push({
      event: 'show_phone_number',
      app: 'aggregator',
      aggregator: listing.url?.originalDomain,
      id: listing.id,
      slug: listing.slug,
      offer_type: listing.offerType,
      page: window.location.href,
      page_type: 'listing-detail',
      agency_id: agencyId,
      agency_name: listing.agencyName,
      agency_reference: listing.agencyReference,
      agency_contact_phone_number: listing.agencyContactPhoneNumber,
      visit_contact_person: listing.visitContactPerson,
      visit_contact_phone_number: listing.visitContactPhoneNumber,
    });
  };

  const formatCurrency = (val: number) =>
    numberLocale.format(val, {
      style: 'currency',
      currency: currency ?? fallbackCurrency,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

  const realPrice = offerType === 'buy' ? salePrice : grossRentMonthly;

  const formattedPrice =
    typeof realPrice === 'number'
      ? `${formatCurrency(realPrice)}${countryDomain !== 'fr' ? '.-' : ''}${
          offerType === 'rent' ? t('monthly') : ''
        }`
      : t('priceOnRequest');

  const pricePerM2 =
    offerType !== 'rent'
      ? typeof salePricePerComputedSurface === 'number'
        ? formatCurrency(salePricePerComputedSurface)
        : `${currency ?? fallbackCurrency} -`
      : typeof grossRentM2yearly === 'number'
      ? formatCurrency(grossRentM2yearly)
      : `${currency ?? fallbackCurrency} -`;

  const formattedPricePerM2 = [
    pricePerM2,
    offerType !== 'rent' ? '/ m²' : t('totalRentM2Year'),
  ].join(' ');

  return (
    <>
      <Card elevation={14} css={{ maxWidth: '328px' }}>
        <Box p={3}>
          <Box css={[text.h5, { whiteSpace: 'nowrap' }]}>{formattedPrice}</Box>
          <Flex>
            <Box flexGrow={1} css={[text.body2]}>
              {formattedPricePerM2}
            </Box>
          </Flex>

          <Box width={1} mt={3}>
            <Divider />

            <Flex css={{ gap: '8px' }} mt={1} pt={3} pb={1}>
              {agencyLogoUrl != null && (
                <Box
                  flexShrink={0}
                  alignSelf="flex-start"
                  css={{
                    border: `1px solid ${colors.grey300}`,
                    borderRadius: shape.borderRadius,
                    padding: '12px',
                  }}
                >
                  <Image
                    src={agencyLogoUrl}
                    objectFit="contain"
                    css={{ width: '3.5rem', height: '3.5rem' }}
                  />
                </Box>
              )}

              <Box>
                <Box css={[text.subtitle1, { lineHeight: 1.25 }]}>
                  {agencyName ?? '-'}
                </Box>

                {agencyRating != null && (
                  <RatingComponent
                    rating={agencyRating}
                    reviewsCount={agencyReviewsCount}
                  />
                )}

                {agencySlug != null && (
                  <Link href={getAgencyLink(language, agencySlug)}>
                    <a
                      css={[
                        text.body2,
                        text.linkToggle,
                        {
                          fontWeight: 'bold',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        },
                      ]}
                    >
                      {t('viewAgencyProfile')}
                    </a>
                  </Link>
                )}
              </Box>
            </Flex>

            {(visitContactPhoneNumber != null ||
              agencyContactPhoneNumber != null) && (
              <Box mt={1} pt={2} pb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Phone />}
                  component="a"
                  onClick={
                    !showPhoneNumber
                      ? () => handleShowPhoneNumber(setShowPhoneNumber)
                      : undefined
                  }
                  href={
                    showPhoneNumber
                      ? `tel:${
                          visitContactPhoneNumber ??
                          agencyContactPhoneNumber ??
                          ''
                        }`
                      : undefined
                  }
                >
                  {showPhoneNumber
                    ? visitContactPhoneNumber ?? agencyContactPhoneNumber ?? ''
                    : t('showNumber')}
                </Button>
              </Box>
            )}
          </Box>

          {props.isListingCampaign === true && (
            <Box width={1} mt={3}>
              <Divider />
              <AggregatesListingCampaignEnquiry listing={listing} />
            </Box>
          )}

          {offlineSince == null &&
            urls[0] != null &&
            props.isListingCampaign !== true && (
              <Box width={1} mt={3}>
                <Divider />
                <Flex justifyContent="space-between" my={3}>
                  <b>
                    {t('foundOn', {
                      url: domainToSitename(urls[0].originalDomain),
                    })}
                  </b>
                  <span css={[text.caption]}>
                    {formatDistanceStrict(parseISO(createdAt), new Date(), {
                      locale: dateLocale,
                      addSuffix: true,
                    })}
                  </span>
                </Flex>
                <Button
                  color="primary"
                  onClick={() => props.onLinkoutClick(urls[0])}
                >
                  {bookOnline === true ? t('bookOnline') : t('contactAgent')}
                </Button>
              </Box>
            )}

          <QueryRenderer
            environment={environment}
            query={graphql`
              query AggregatesListingPriceCardMeQuery {
                me {
                  isAdmin
                }
              }
            `}
            variables={{}}
            render={({
              props: data,
              error,
            }: RenderProps<AggregatesListingPriceCardMeQueryResponse>) =>
              (data != null || error != null) &&
              data?.me?.isAdmin === true && (
                <AggregatesListingPriceCardContent listing={listing} />
              )
            }
          />
        </Box>
      </Card>
    </>
  );
};
